import * as React from "react";
import {cva, type VariantProps} from "class-variance-authority";

import {cn} from "@/lib/utils";
import {X} from "lucide-react";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        primary:
          "bg-primary-background text-primary-dark border border-border-dark-20",
        info: "bg-blue-700/8 border-blue-700/20 text-blue-900 dark:border-blue-700 [&>svg]:text-blue-900",
        warning:
          "bg-warning/8 bg border-warning/20 text-warning [&>svg]:text-warning",
        destructive: "border-red/20 text-red-700 bg-red/8 [&>svg]:text-red-700",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({className, variant, ...props}, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({variant}), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({className, ...props}, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

const AlertClose = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {closeAlert: () => void}
>(({className, closeAlert, ...props}, ref) => (
  <div
    ref={ref}
    onClick={closeAlert}
    className={cn("absolute right-3 top-3 cursor-pointer", className)}
    {...props}
  >
    <X className="h-4.5 w-4.5" />
  </div>
));
AlertClose.displayName = "AlertClose";

export {Alert, AlertTitle, AlertClose, AlertDescription};
